import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DocumentStatusBadge } from '@/components/shared/DocumentStatusBadge';
import { HorizontalStepTracker } from '@/components/shared/HorizontalStepTracker';
import { 
  Building2, 
  Eye, 
  Plus,
  Clock,
  MapPin,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { llcAPI } from '@/services/api';
import { toast } from 'sonner';

export default function MyLLCRequests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myLLCs, setMyLLCs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLLCs = async () => {
      try {
        setLoading(true);
        const data = await llcAPI.getLLCs();
        setMyLLCs(data);
      } catch (error) {
        console.error('Error fetching LLC formations:', error);
        toast.error('Failed to load LLC formations');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchLLCs();
    }
  }, [user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="My LLC Requests" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="My LLC Requests" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'LLC Formation', href: '/llc/dashboard' },
          { label: 'My Requests' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">My LLC Formation Requests</h2>
            <p className="mt-1 text-muted-foreground">Track and manage your LLC applications</p>
          </div>
          <Button 
            className="btn-professional gap-2"
            onClick={() => navigate('/llc/create')}
          >
            <Plus className="h-4 w-4" />
            New LLC Formation
          </Button>
        </div>

        {/* LLC Requests Grid */}
        <div className="grid gap-6 md:grid-cols-1">
          {myLLCs.map((llc) => (
            <Card 
              key={llc.id} 
              className="tax-card cursor-pointer transition-all hover:shadow-lg"
              onClick={() => navigate(`/llc/requests/${llc.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="rounded-lg bg-primary/10 p-3">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-foreground">{llc.company_name}</h3>
                        <DocumentStatusBadge status={llc.status} />
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {llc.state}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Submitted {new Date(llc.created_at).toLocaleDateString()}
                        </div>
                        <Badge variant="outline">{llc.company_info?.selectedPackage || 'Standard'}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">
                      {llc.company_info?.price || '-'}
                    </p>
                    {llc.status === 'completed' && llc.updated_at && (
                      <div className="mt-2 flex items-center gap-1 text-sm text-success">
                        <CheckCircle2 className="h-4 w-4" />
                        Completed {new Date(llc.updated_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {llc.status === 'completed' 
                      ? 'All documents available for download' 
                      : llc.status === 'in_review'
                      ? 'Agent is reviewing your application'
                      : 'Waiting for processing to begin'}
                  </div>
                  <Button variant="ghost" size="sm" className="gap-2">
                    View Details
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {myLLCs.length === 0 && (
          <Card className="tax-card border-dashed">
            <CardContent className="pt-12 pb-12 text-center">
              <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No LLC Requests Yet</h3>
              <p className="text-muted-foreground mb-6">Start your business formation journey today</p>
              <Button 
                className="btn-professional"
                onClick={() => navigate('/llc/create')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First LLC
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
